import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAccountTransactionDrawerStore } from '@hero/hooks-shared';
import { useAccountTransactionApi } from '@hero/api-hooks';
import type { AccountTransaction } from '@hero/schema';
import { Price } from '../../../app/Price';
import { StackedDrawer } from '../../../app/Drawer/StackedDrawer';
import { DrawerHeader } from '../../../app/Drawer/DrawerHeader';
import { DrawerTabs } from '../../../app/Drawer/DrawerTabs';
import { DrawerContent } from '../../../app/Drawer/DrawerContent';
import { AccountTransactionSummaryTab } from './AccountTransactionSummaryTab';
import { AccountTransactionInvoicesTab } from './AccountTransactionInvoicesTab';
import { AccountTransactionOrdersTab } from './AccountTransactionOrdersTab';

export interface AccountTransactionDrawerProps {
  syncUrl?: boolean | string;
}

interface AccountTransactionDrawerContentProps {
  transaction: AccountTransaction;
  onClose: () => void;
}

const AccountTransactionDrawerContent: React.FC<AccountTransactionDrawerContentProps> = ({
  transaction,
  onClose,
}) => {
  const { t } = useTranslation(['m-account-transaction', 'm-payment']);
  const [activeTab, setActiveTab] = useState('summary');

  const typeLabel = t(`type.${transaction.type}`, { defaultValue: transaction.type });

  const tabs: { id: string; label: string }[] = [
    {
      id: 'summary',
      label: t('drawer.tabs.summary', { defaultValue: 'Summary' }),
    },
    {
      id: 'invoices',
      label: t('drawer.tabs.invoices', { defaultValue: 'Invoices' }),
    },
    {
      id: 'orders',
      label: t('drawer.tabs.orders', { defaultValue: 'Orders' }),
    },
  ];

  const renderTab = () => {
    switch (activeTab) {
      case 'summary':
        return <AccountTransactionSummaryTab transaction={transaction} />;
      case 'invoices':
        return <AccountTransactionInvoicesTab transaction={transaction} />;
      case 'orders':
        return <AccountTransactionOrdersTab transaction={transaction} />;
      default:
        return null;
    }
  };

  return (
    <>
      <DrawerHeader
        title={transaction.ref.main || transaction.ref.short}
        subtitle={
          <>
            <Price
              amount={Math.abs(transaction.amount)}
              currency={transaction.currency}
            />
            {' · '}
            {typeLabel}
          </>
        }
        onClose={onClose}
      />
      <DrawerTabs tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} />
      <DrawerContent>{renderTab()}</DrawerContent>
    </>
  );
};

/**
 * Self-contained Account Transaction Drawer.
 *
 * Mount ONE instance per host page. Open from anywhere via:
 * ```ts
 * useAccountTransactionDrawerStore.getState().open(transactionId)
 * ```
 */
export const AccountTransactionDrawer: React.FC<AccountTransactionDrawerProps> = ({
  syncUrl,
}) => (
  <StackedDrawer<AccountTransaction>
    useStore={useAccountTransactionDrawerStore}
    useItemApi={id => useAccountTransactionApi(id)}
    syncUrl={syncUrl}
    defaultParamKey="accountTransactionId"
    sheetClassName="flex flex-col p-0 bg-white [&>button[class*='absolute']]:hidden"
    title="Account Transaction Details"
    description="View account transaction information"
    renderContent={(transaction, onClose) => (
      <AccountTransactionDrawerContent transaction={transaction} onClose={onClose} />
    )}
  />
);
